import React from "react";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { useAmp } from "next/amp";
import Link from "next/link";
import Head from "next/head";
import PageLayoutV1 from "@app/hocs/PageLayoutV1";
import * as CmsHttp from "@app/http/cms";
import { sliceMetaDescription } from "@app/utilities/helpers";
import { META_DESCRIPTION, META_TITLE, META_URL, REVALIDATE_SECONDS } from "@app/utilities/constants";
import { Blog } from "@models/Blog";
import SimpleExpertLinkCard from "@components/experts/SimpleExpertLinkCard";
import SocialShareLinks from "@components/socials/SocialShareLinks";
import { Breadcrumbs } from "@components/navs/Breadcrumbs";
import { CmsMeta } from "@app/http/cms/components/CmsMeta";
import { withUpdateCmsVisit } from "@app/hooks/WithUpdateCmsVisit";

interface IBlogPostPage {
  blogPost: Blog;
}

const BlogPostPage: NextPage<IBlogPostPage> = (props: IBlogPostPage) => {
  const { blogPost } = props,
    isAmp = useAmp(),
    pageUri = "/blogs/" + blogPost.slug,
    title = blogPost.title,
    blogMetaDescription = sliceMetaDescription(blogPost?.content?.text);

  /**
   * Meta contents
   */
  let metaTitle = META_TITLE || "",
    metaDescription = META_DESCRIPTION || "",
    metaUrl = META_URL || "",
    metaOgImg = META_URL + `/logo.png`,
    metaTwitterImg = META_URL + `/logo.png`;

  if (blogPost) {
    metaTitle = `${blogPost.title} | Heritage Counseling`;
    metaDescription = blogMetaDescription || metaDescription;
    metaUrl = `${META_URL}/blogs/${blogPost.slug}`;
    metaOgImg = blogPost.featuredImage?.url || metaOgImg;
    metaTwitterImg = blogPost.featuredImage?.url || metaTwitterImg;
  }

  // Override metas using pageMeta
  const pageMeta = (blogPost?.meta || {}) as CmsMeta;
  if (Object.keys(pageMeta).length > 0) {
    metaTitle = pageMeta?.title || metaTitle;
    metaDescription = pageMeta?.description || metaDescription;
    metaOgImg = pageMeta?.ogImage?.url || metaOgImg;
    metaTwitterImg = pageMeta?.twitterImage?.url || metaTwitterImg;
  }

  return isAmp && blogPost ? (
    <div className="blog-post-page" data-testid="blog-post-page-amp">
      <Head>
        <title>{metaTitle}</title>
        <meta key="title" name="title" content={metaTitle} />
        <meta key="description" name="description" content={metaDescription} />

        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:url" property="og:url" content={metaUrl} />
        <meta key="og:image" property="og:image" content={metaOgImg} />

        <meta key="twitter:url" name="twitter:url" content={metaUrl} />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        <meta key="twitter:image" name="twitter:image" content={metaTwitterImg} />

        <meta key="prop:title" itemProp="title" content={metaTitle} />
        <meta key="prop:description" itemProp="description" content={metaDescription} />

        <link rel="canonical" href={metaUrl} />
      </Head>
      <div className="wrapper" itemScope itemType="https://schema.org/Article">
        <meta itemProp="wordcount" content={blogPost.schema.wordcount} />
        <meta itemProp="image" content={blogPost.schema.image} />
        <meta itemProp="datePublished" content={blogPost.schema.datePublished} />
        <meta itemProp="dateCreated" content={blogPost.schema.dateCreated} />
        <meta itemProp="dateModified" content={blogPost.schema.dateModified} />
        <div className="blog-body">
          <Breadcrumbs uri={pageUri} isAmp={isAmp} />
          <h1 itemProp="headline">{blogPost.title}</h1>
          {!!blogPost.featuredImage?.src && (
            <div className="img-wrapper">
              <amp-img alt={blogPost.title} src={blogPost.featuredImage?.url} width={blogPost.featuredImage?.width} height={blogPost.featuredImage?.height} layout="responsive"></amp-img>
            </div>
          )}
          <div className="blog-content cms-content" itemProp="articleBody" dangerouslySetInnerHTML={{ __html: blogPost.content.html }} />
        </div>
        <div className="blog-side">
          <SocialShareLinks url={metaUrl} isAmp={isAmp} />
          {blogPost?.author && blogPost?.author?.firstName && (
            <div className="author">
              <h5> Author </h5>
              <SimpleExpertLinkCard expert={blogPost.author} isAmp={isAmp} />
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <PageLayoutV1 title={title}>
      <Head>
        <title>{metaTitle}</title>
        <meta key="title" name="title" content={metaTitle} />
        <meta key="description" name="description" content={metaDescription} />

        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:url" property="og:url" content={metaUrl} />
        <meta key="og:image" property="og:image" content={metaOgImg} />

        <meta key="twitter:url" name="twitter:url" content={metaUrl} />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        <meta key="twitter:image" name="twitter:image" content={metaTwitterImg} />

        <meta key="prop:title" itemProp="title" content={metaTitle} />
        <meta key="prop:description" itemProp="description" content={metaDescription} />

        <link rel="amphtml" href={metaUrl + "?amp=1"} />
        <link rel="canonical" href={metaUrl} />
        {blogPost?.schema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(blogPost.schema),
            }}
          ></script>
        )}
      </Head>
      <div className="blog-post-page" data-testid="blog-post-page">
        {blogPost ? (
          <div className="wrapper">
            <div className="blog-body">
              <Breadcrumbs uri={pageUri} isAmp={isAmp} />
              {!!blogPost.featuredImage?.src && (
                <div className="img-wrapper">
                  <img className="featured-img" src={blogPost.featuredImage?.src} alt={blogPost.title} />
                </div>
              )}
              <div className="blog-content cms-content" dangerouslySetInnerHTML={{ __html: blogPost.content.html }} />
            </div>
            <div className="blog-side">
              <SocialShareLinks url={metaUrl} isAmp={isAmp} />
              <div className="author">
                <h5> Author </h5>
                <SimpleExpertLinkCard expert={blogPost.author} />
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center">No blog found</p>
        )}
        <nav className="mt-5 mt-lg-10" role="navigation">
          <Link href="/">
            <a className="link-back">
              <i className="fa fa-angle-left"></i>
              Back to Home
            </a>
          </Link>
        </nav>
      </div>
    </PageLayoutV1>
  );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = (params && Array.isArray(params?.slug) ? params.slug[0] : params?.slug) as string,
    blogPost = await CmsHttp.getBlogBySlug(slug);

  return {
    props: {
      blogPost: blogPost || null,
    } as IBlogPostPage,
    notFound: !blogPost,
    revalidate: REVALIDATE_SECONDS, // In seconds
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const allBlogs = [];
  let page = 1;
  let hasMore = true;

  // Recursively fetch all blog pages
  while (hasMore) {
    const result = await CmsHttp.getBlogs(page, 100);
    if (result.blogs && result.blogs.length) {
      allBlogs.push(...result.blogs);
      if (result.blogs.length < result.totalCount) {
        hasMore = false;
      }
    } else {
      hasMore = false;
    }
    page++;
  }

  const slugs = allBlogs.map((blog) => blog?.slug || "").filter(Boolean);

  return {
    paths: slugs.map((slug) => ({
      params: {
        slug,
      },
    })),
    fallback: "blocking",
  };
};

export const config = { amp: "hybrid" };
export default withUpdateCmsVisit(BlogPostPage);
